import { useEffect } from "react";

export const SITE_URL = "https://meucontrolhe.app";
export const SITE_NAME = "Meu Controle";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-meu-controlhe-1200x630.jpg`;

type SeoProps = {
  /** Título completo da aba/SERP (até ~60 caracteres). */
  title: string;
  /** Meta description (até ~160 caracteres). */
  description: string;
  /** Caminho da rota, ex: "/blog". */
  path: string;
  type?: "website" | "article";
  image?: string;
  /** Texto alternativo da imagem social. */
  imageAlt?: string;
  noindex?: boolean;
  /** Um ou mais objetos JSON-LD injetados no <head>. */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  publishedTime?: string;
  modifiedTime?: string;
};

function upsertMeta(attr: "name" | "property", key: string, value: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  let created = false;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
    created = true;
  }
  const prev = el.getAttribute("content");
  el.setAttribute("content", value);
  return () => {
    if (created) el!.remove();
    else if (prev !== null) el!.setAttribute("content", prev);
  };
}

/**
 * Gerencia todo o <head> por rota: title, description, canonical,
 * Open Graph, Twitter Cards e dados estruturados (JSON-LD).
 */
export function Seo({
  title,
  description,
  path,
  type = "website",
  image = DEFAULT_OG_IMAGE,
  imageAlt,
  noindex = false,
  jsonLd,
  publishedTime,
  modifiedTime,
}: SeoProps) {
  const url = `${SITE_URL}${path}`;
  const graph = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  const graphKey = JSON.stringify(graph);

  useEffect(() => {
    const cleanups: Array<() => void> = [];

    const prevTitle = document.title;
    document.title = title;
    cleanups.push(() => {
      document.title = prevTitle;
    });

    cleanups.push(upsertMeta("name", "description", description));
    cleanups.push(upsertMeta("property", "og:title", title));
    cleanups.push(upsertMeta("property", "og:description", description));
    cleanups.push(upsertMeta("property", "og:type", type));
    cleanups.push(upsertMeta("property", "og:url", url));
    cleanups.push(upsertMeta("property", "og:site_name", SITE_NAME));
    cleanups.push(upsertMeta("property", "og:locale", "pt_BR"));
    cleanups.push(upsertMeta("property", "og:image", image));
    cleanups.push(upsertMeta("property", "og:image:secure_url", image));
    cleanups.push(upsertMeta("property", "og:image:type", "image/jpeg"));
    cleanups.push(upsertMeta("property", "og:image:width", "1200"));
    cleanups.push(upsertMeta("property", "og:image:height", "630"));
    cleanups.push(upsertMeta("property", "og:image:alt", imageAlt ?? title));
    cleanups.push(upsertMeta("name", "twitter:card", "summary_large_image"));
    cleanups.push(upsertMeta("name", "twitter:title", title));
    cleanups.push(upsertMeta("name", "twitter:description", description));
    cleanups.push(upsertMeta("name", "twitter:image", image));
    cleanups.push(upsertMeta("name", "twitter:image:alt", imageAlt ?? title));
    cleanups.push(upsertMeta("name", "twitter:site", "@fluxsistemas"));
    cleanups.push(upsertMeta("name", "twitter:creator", "@fluxsistemas"));
    cleanups.push(upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1"));

    if (publishedTime) cleanups.push(upsertMeta("property", "article:published_time", publishedTime));
    if (modifiedTime) cleanups.push(upsertMeta("property", "article:modified_time", modifiedTime));

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    const prevCanonical = canonical.getAttribute("href");
    canonical.setAttribute("href", url);
    cleanups.push(() => {
      if (prevCanonical) canonical!.setAttribute("href", prevCanonical);
    });

    const scripts = graph.map((data) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.dataset.seo = "route";
      s.text = JSON.stringify(data);
      document.head.appendChild(s);
      return s;
    });
    cleanups.push(() => scripts.forEach((s) => s.remove()));

    return () => cleanups.reverse().forEach((fn) => fn());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, url, type, image, imageAlt, noindex, graphKey, publishedTime, modifiedTime]);

  return null;
}

export default Seo;
